import { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import type { HandlerFn } from "..";
import type { ExecutorCommand } from "../../../../Executor";
import { resolvePks } from "./executor";
import { Modal } from "../../../utils/modal";
import { SelectStatic } from "../../../select";

interface Policy {
  id: number;
  name: string;
}

async function fetchPolicies(): Promise<{ data: Policy[]; error?: string }> {
  try {
    const res = await fetch("/automation/api/policies/", {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (!res.ok) {
      return { data: [], error: `Ошибка загрузки политик (${res.status})` };
    }
    const data = await res.json();
    return { data: data.results ?? data };
  } catch {
    return { data: [], error: "Не удалось загрузить список политик" };
  }
}

function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M6.667 4.167V15.833L15.833 10L6.667 4.167Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function PolicySelectModal({
  onConfirm,
  onClose,
}: {
  onConfirm: (policyId: number) => void;
  onClose: () => void;
}) {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPolicies().then((result) => {
      setPolicies(result.data);
      if (result.error) setError(result.error);
      setLoading(false);
    });
  }, []);

  return (
    <Modal
      show={true}
      onClose={onClose}
      title="Выберите политику"
      subtitle="Запуск политики"
      size="sm"
      bodyClassName="rd-modal__body--overflow-visible"
      footer={
        <>
          <button
            className="rd-btn rd-btn--green rd-btn--large"
            disabled={!selectedId}
            onClick={() => selectedId && onConfirm(Number(selectedId))}
          >
            <PlayIcon />
            Запустить в терминале
          </button>
          <button
            className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--large"
            onClick={onClose}
          >
            Отмена
          </button>
        </>
      }
    >
      {error && <div className="rd-input rd-input--error" style={{ marginBottom: 12 }}><span className="rd-input__error-text">{error}</span></div>}
      <SelectStatic
        name="policy"
        mode="static"
        size="large"
        searchable
        options={policies.map((p) => ({
          value: String(p.id),
          label: p.name,
        }))}
        placeholder={loading ? "Загрузка..." : "Выбрать..."}
        disabled={loading || !!error}
        onChange={setSelectedId}
      />
    </Modal>
  );
}

export const executorWithPolicyHandler: HandlerFn = (ctx) => {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const cleanup = () => {
    try { root.unmount(); } catch { /* already unmounted */ }
    container.remove();
  };

  const handleConfirm = (policyId: number) => {
    cleanup();

    const executor: ((cmd: ExecutorCommand) => void) | undefined =
      (window as any).executor;
    if (!executor) return;

    const pkParam = ctx.config?.pk_param ?? "host_pk";
    const pks = resolvePks(ctx);
    if (!ctx.selectAcross && pks.length === 0) return;

    const hostParam =
      ctx.selectAcross && pks.length === 0
        ? "all"
        : pks.length > 1
          ? pks.join(",")
          : pks[0]!;

    executor({
      params: {
        kind: ctx.config?.executor_kind,
        [pkParam]: hostParam,
        policy_id: policyId,
        ...ctx.optionValues,
      },
      modal: ctx.config?.modal_title,
    });
  };

  root.render(
    <PolicySelectModal onConfirm={handleConfirm} onClose={cleanup} />,
  );
};
